import { useMemo, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { BookOpen, Check, MapPin, Search } from 'lucide-react'
import type { ComponentStatus, CurriculumNodeView } from '@/types/coaching'

export interface LessonPlacementOption {
  lessonId: number
  label: string
  title: string
  status: ComponentStatus
  curriculumId: number
  curriculumTitle: string
  courseId: number
  courseTitle: string
  moduleId: number
  chapterNumber: number
  chapterTitle: string
  lessonNumber: number
}

interface Props {
  id: string
  label: string
  options: LessonPlacementOption[]
  value: number | null
  onChange: (lessonId: number) => void
}

interface CurriculumGroup {
  curriculumId: number
  title: string
  courses: CourseGroup[]
}

interface CourseGroup {
  courseId: number
  title: string
  chapters: ChapterGroup[]
}

interface ChapterGroup {
  moduleId: number
  chapterNumber: number
  title: string
  lessons: LessonPlacementOption[]
}

/** 코스·챕터 맥락을 유지하는 레슨 진행 위치 선택기. */
export function LessonPlacementPicker({ id, label, options, value, onChange }: Props) {
  const { t } = useTranslation('coaching')
  const [query, setQuery] = useState('')
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const selected = options.find((option) => option.lessonId === value) ?? null
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return options
    return options.filter((option) => (
      [
        option.curriculumTitle,
        option.courseTitle,
        option.chapterTitle,
        option.title,
        `${option.chapterNumber}-${option.lessonNumber}`,
      ].some((text) => text.toLocaleLowerCase().includes(normalizedQuery))
    ))
  }, [normalizedQuery, options])
  const groups = useMemo(() => groupOptions(filteredOptions), [filteredOptions])
  const labelId = `${id}-label`

  return (
    <div className="flex flex-col gap-2.5">
      <p id={labelId} className="text-xs font-semibold text-gray-700">{label}</p>

      {selected ? (
        <div className="rounded-xl border border-primary-200 bg-primary-50/70 px-3.5 py-3" aria-live="polite">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white">
              <MapPin className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-primary-700">
                {t('lessonPlacement.selected')}
              </p>
              <p className="mt-0.5 truncate text-xs text-gray-600">
                <ContentMetadataText text={selected.curriculumTitle} /> · <ContentMetadataText text={selected.courseTitle} /> · <ContentMetadataText text={t('lessonPlacement.chapter', {
                  number: selected.chapterNumber,
                  title: selected.chapterTitle,
                })} />
              </p>
              <p className="mt-0.5 text-sm font-bold text-gray-950">
                {selected.chapterNumber}-{selected.lessonNumber}. <ContentMetadataText text={selected.title} />
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/70">
        <div className="border-b border-gray-200 bg-white p-2.5">
          <label className="relative block" htmlFor={`${id}-search`}>
            <span className="sr-only">{t('lessonPlacement.searchLabel')}</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              id={`${id}-search`}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('lessonPlacement.searchPlaceholder')}
              autoComplete="off"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
          </label>
        </div>

        <div
          role="radiogroup"
          aria-labelledby={labelId}
          className="max-h-72 overflow-y-auto overscroll-contain p-2.5"
        >
          {groups.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-gray-500" role="status">
              {t('lessonPlacement.noResults')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {groups.map((curriculum) => (
                <section key={curriculum.curriculumId}>
                  <p className="mb-1.5 px-1 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                    <ContentMetadataText text={curriculum.title} />
                  </p>
                  <div className="flex flex-col gap-2">
                    {curriculum.courses.map((course) => (
                      <div key={course.courseId} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
                          <BookOpen className="size-4 shrink-0 text-primary-600" aria-hidden="true" />
                          <p className="truncate text-sm font-bold text-gray-900"><ContentMetadataText text={course.title} /></p>
                        </div>
                        <div className="flex flex-col gap-2 p-2">
                          {course.chapters.map((chapter) => (
                            <section key={chapter.moduleId}>
                              <div className="mb-1 flex items-center justify-between gap-2 px-1.5">
                                <h4 className="truncate text-xs font-bold text-gray-600">
                                  <ContentMetadataText text={t('lessonPlacement.chapter', {
                                    number: chapter.chapterNumber,
                                    title: chapter.title,
                                  })} />
                                </h4>
                                <span className="shrink-0 text-[11px] font-medium text-gray-400">
                                  {t('lessonPlacement.lessonCount', { count: chapter.lessons.length })}
                                </span>
                              </div>
                              <div className="flex flex-col gap-1">
                                {chapter.lessons.map((lesson) => {
                                  const checked = lesson.lessonId === value
                                  return (
                                    <label
                                      key={lesson.lessonId}
                                      className={clsx(
                                        'flex cursor-pointer items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors',
                                        checked
                                          ? 'border-primary-300 bg-primary-50 text-gray-950'
                                          : 'border-transparent text-gray-700 hover:border-gray-200 hover:bg-gray-50',
                                      )}
                                    >
                                      <input
                                        type="radio"
                                        name={`${id}-option`}
                                        value={lesson.lessonId}
                                        checked={checked}
                                        onChange={() => onChange(lesson.lessonId)}
                                        aria-label={`${lesson.courseTitle}, ${t('lessonPlacement.chapter', {
                                          number: lesson.chapterNumber,
                                          title: lesson.chapterTitle,
                                        })}, ${lesson.chapterNumber}-${lesson.lessonNumber}. ${lesson.title}`}
                                        className="sr-only"
                                      />
                                      <span
                                        className={clsx(
                                          'flex size-5 shrink-0 items-center justify-center rounded-full border',
                                          checked
                                            ? 'border-primary-600 bg-primary-600 text-white'
                                            : 'border-gray-300 bg-white',
                                        )}
                                        aria-hidden="true"
                                      >
                                        {checked ? <Check className="size-3.5" /> : null}
                                      </span>
                                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                                        {lesson.chapterNumber}-{lesson.lessonNumber}. <ContentMetadataText text={lesson.title} />
                                      </span>
                                      {lesson.status === 'in_progress' ? (
                                        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                                          {t('lessonPlacement.current')}
                                        </span>
                                      ) : null}
                                    </label>
                                  )
                                })}
                              </div>
                            </section>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function buildLessonPlacementOptions(
  tree: CurriculumNodeView[],
  selectable: (status: ComponentStatus) => boolean,
): LessonPlacementOption[] {
  const options: LessonPlacementOption[] = []
  for (const curriculum of tree) {
    for (const course of curriculum.courses) {
      for (const module of course.modules) {
        module.lessons.forEach((lesson, index) => {
          if (!selectable(lesson.status)) return
          options.push({
            lessonId: lesson.lessonId,
            label: `${module.chapterNumber}-${index + 1}. ${lesson.title}`,
            title: lesson.title,
            status: lesson.status,
            curriculumId: curriculum.curriculumId,
            curriculumTitle: curriculum.title,
            courseId: course.courseId,
            courseTitle: course.title,
            moduleId: module.moduleId,
            chapterNumber: module.chapterNumber,
            chapterTitle: module.title,
            lessonNumber: index + 1,
          })
        })
      }
    }
  }
  return options
}

function groupOptions(options: LessonPlacementOption[]): CurriculumGroup[] {
  const curricula = new Map<number, CurriculumGroup>()
  for (const option of options) {
    let curriculum = curricula.get(option.curriculumId)
    if (!curriculum) {
      curriculum = { curriculumId: option.curriculumId, title: option.curriculumTitle, courses: [] }
      curricula.set(option.curriculumId, curriculum)
    }

    let course = curriculum.courses.find((item) => item.courseId === option.courseId)
    if (!course) {
      course = { courseId: option.courseId, title: option.courseTitle, chapters: [] }
      curriculum.courses.push(course)
    }

    let chapter = course.chapters.find((item) => item.moduleId === option.moduleId)
    if (!chapter) {
      chapter = {
        moduleId: option.moduleId,
        chapterNumber: option.chapterNumber,
        title: option.chapterTitle,
        lessons: [],
      }
      course.chapters.push(chapter)
    }
    chapter.lessons.push(option)
  }
  return Array.from(curricula.values())
}
